/**
 * PER-SPECIES DECOR — the reward pieces that honour one plant, fish or insect: the Botanical Marker and the
 * Gold Statue (DECOR entries flagged `perSpecies`). One catalogue entry each; every placed copy carries its
 * species (DecorPlacement.species, "plant:rose" / "fish:koi" / "insect:monarch"), and the bag counts them per
 * species under a key "id:kind:species" (sim/decor.ts decorKey).
 *
 * The species' PICTURE is the game's own pre-baked icon (public/icons/<kind>/<id>.png) — the same art the
 * inventory and the achievements show — recoloured by the renderer (brass inlay, cast gold), so a species
 * added to the roster needs no new decor art.
 */
import { DECOR, REWARD_DECOR_IDS, BACKER_DECOR_IDS, parseDecorKey, type DecorId } from "./decor";
import { FLOWER_IDS, flower, type FlowerId } from "./flowers";
import { FISH } from "./fishing";
import { INSECTS } from "./insects";

export type SpeciesKind = "plant" | "fish" | "insect";
export const PER_SPECIES_DECOR: DecorId[] = (Object.keys(DECOR) as DecorId[]).filter((id) => DECOR[id].perSpecies);

export function speciesRef(kind: SpeciesKind, id: string): string { return `${kind}:${id}`; }
export function parseSpeciesRef(ref: string): { kind: SpeciesKind; id: string } | null {
  const i = ref.indexOf(":");
  const kind = ref.slice(0, i) as SpeciesKind, id = ref.slice(i + 1);
  return i > 0 && (kind === "plant" || kind === "fish" || kind === "insect") && id ? { kind, id } : null;
}
/** The species' display name (the ref itself if it is not on the roster). */
export function speciesName(ref: string): string {
  const p = parseSpeciesRef(ref);
  if (!p) return ref;
  if (p.kind === "plant") { try { return flower(p.id as FlowerId).name; } catch { return p.id; } }
  if (p.kind === "fish") return FISH.find((f) => f.id === p.id)?.name ?? p.id;
  return INSECTS.find((b) => b.id === p.id)?.name ?? p.id;
}
/** The pre-baked icon the art is built from (public/icons). */
export function speciesIconUrl(ref: string): string {
  const p = parseSpeciesRef(ref);
  return p ? `/icons/${p.kind}/${p.id}.png` : "";
}
/** Every species ref on the roster — plants, then fish, then insects. */
export function allSpeciesRefs(): string[] {
  return [
    ...FLOWER_IDS.map((id) => speciesRef("plant", id)),
    ...FISH.map((f) => speciesRef("fish", f.id)),
    ...INSECTS.map((b) => speciesRef("insect", b.id)),
  ];
}
/** What a placed per-species piece is called: "Rose Botanical Marker", "Gold Koi Statue". */
export function speciesDecorName(decor: DecorId, ref: string): string {
  const n = speciesName(ref);
  return decor === "goldStatue" ? `Gold ${n} Statue` : `${n} ${DECOR[decor].name}`;
}

/** How many of each REWARD-only decor item the dev Requirement Override adds to the bag (never saved). */
export const DEV_REWARD_STOCK = 20;
/** The bag as the palette and the place flow should SEE it. With the dev Requirement Override on, every
 *  reward-only item (and every Kickstarter backer piece) gets DEV_REWARD_STOCK on top of what is owned — and every PER-SPECIES item one of each
 *  species — so it can be placed and judged without earning it. A VIEW: never write it back to `decorOwned`. */
export function decorStockView(owned: Record<string, number>, override: boolean): Record<string, number> {
  if (!override) return owned;
  const o = { ...owned };
  for (const id of REWARD_DECOR_IDS) if (!DECOR[id].perSpecies) o[id] = (o[id] ?? 0) + DEV_REWARD_STOCK;
  for (const id of BACKER_DECOR_IDS) o[id] = (o[id] ?? 0) + DEV_REWARD_STOCK;   // backer pieces, to place and judge
  const refs = allSpeciesRefs();
  for (const id of PER_SPECIES_DECOR) for (const r of refs) { const k = `${id}:${r}`; o[k] = (o[k] ?? 0) + 1; }
  return o;
}

/** A display name for any bag key — a plain decor id or a per-species one ("goldStatue:fish:koi"). */
export function decorKeyName(key: string): string {
  const i = key.indexOf(":");
  if (i < 0) return (DECOR as Record<string, { name: string }>)[key]?.name ?? key;
  return speciesDecorName(key.slice(0, i) as DecorId, key.slice(i + 1));
}

/**
 * THE DECOR AN ACHIEVEMENT PAYS, as bag entries — shared by the live grant (App payAchievementRewards) and the
 * one-time catch-up for achievements already held, so the two can never disagree:
 *   • a plain reward item → +n copies (more can then be bought in the shop);
 *   • a TROPHY (per-species key) → owned exactly ONCE: `applyDecorGrants` raises it to 1 and never higher,
 *     so a re-grant (dev revoke/grant, the catch-up running on an item already held) cannot mint a second;
 *   • `topMarkers` → a Botanical Marker for each of the N most-harvested plants.
 * Keys that are not in the catalogue are skipped (rewards arrive from untrusted tuning JSON).
 */
export function decorGrants(
  r: { decor?: Record<string, number>; topMarkers?: number } | undefined,
  harvested: Record<string, number>,
): Array<{ key: string; n: number }> {
  if (!r) return [];
  const out: Array<{ key: string; n: number }> = [];
  for (const [key, n] of Object.entries(r.decor ?? {})) if (n && n > 0 && parseDecorKey(key)) out.push({ key, n });
  if (r.topMarkers) {
    const top = Object.entries(harvested).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]).slice(0, r.topMarkers);
    for (const [id] of top) out.push({ key: `botanicalMarker:plant:${id}`, n: 1 });
  }
  return out;
}
/** Apply grants to an owned-bag map (a new object; the input is not mutated). */
export function applyDecorGrants(owned: Record<string, number>, grants: Array<{ key: string; n: number }>): Record<string, number> {
  const o = { ...owned };
  for (const { key, n } of grants) {
    const p = parseDecorKey(key);
    if (!p) continue;
    o[key] = p.species ? Math.max(o[key] ?? 0, 1) : (o[key] ?? 0) + n;
  }
  return o;
}
